import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRoleViewFetch } from './RoleViewContext';
import './NotarialProjectEditor.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const token = () => localStorage.getItem('token') || sessionStorage.getItem('token');
const FIELD_LABELS = {
  folio: 'Folio', otorgante: 'Otorgante', rfc_otorgante: 'RFC del otorgante',
  notario: 'Notario', numero_notaria: 'Número de notaría',
  lugar_firma: 'Lugar de firma', fecha_firma: 'Fecha de firma',
  apoderados: 'Apoderados', poderes: 'Poderes y facultades',
  fecha_escritura: 'Fecha de escritura', numero_escritura: 'Número de escritura',
};
const describeField = (key) => FIELD_LABELS[key] || key.replace(/_/g, ' ');
const textOf = value => value == null ? '' : String(value);
const readable = value => Array.isArray(value) ? value.map(readable).join(', ') : typeof value === 'object'
  ? Object.values(value).filter(v => typeof v !== 'object' && v != null).map(String).join(' · ')
  : textOf(value);
function flattenAnswers(input, prefix = '', depth = 0) {
  if (input == null || depth > 6) return [];
  if (Array.isArray(input)) return input.flatMap((item, index) =>
    flattenAnswers(item, `${prefix} ${index + 1}`.trim(), depth + 1));
  if (typeof input === 'object') return Object.entries(input).flatMap(([key, value]) =>
    flattenAnswers(value, `${prefix} · ${describeField(key)}`.replace(/^ · /, '').trim(), depth + 1));
  if (input === '') return [];
  return [[prefix || 'Información', typeof input === 'boolean' ? (input ? 'Sí' : 'No') : textOf(input)]];
}

/** Reusable notary questionnaire. Client-provided facts are read-only; changes to
 * deed wording are stored only on the notarial draft, never in the original Acta. */
function QuestionnaireSummary({ questionnaire }) {
  if (!questionnaire) return <p>No está disponible el cuestionario del expediente.</p>;
  const groups = [
    ['Solicitante', [
      ['Folio', questionnaire.folio], ['Cliente', questionnaire.userName],
      ['Correo del cliente', questionnaire.userEmail], ['Empresa / otorgante', questionnaire.companyName],
    ]],
    ['Datos del usuario', flattenAnswers(questionnaire.clientIntake || {})],
    ['Datos de la solicitud', flattenAnswers(questionnaire.generalData || {})],
    ['Datos del Acta', flattenAnswers(Object.fromEntries(Object.entries(questionnaire.assemblyData || {}).filter(([key]) => key !== 'shareholders')))],
    ['Respuestas complementarias del cuestionario', flattenAnswers(questionnaire.answers || {})],
  ];
  return <div className="npe-questionnaire">
    <div className="npe-info-note"><strong>Información original del cliente</strong><p>Esta captura alimentó el Acta Wisby y la plantilla de la Escritura Pública. Para ajustar cómo aparece un valor en esta escritura, usa la pestaña «Placeholders»; no se modifica el Acta original.</p></div>
    {groups.map(([title, entries]) => <section className="npe-question-card" key={title}>
      <h4>{title}</h4><div className="npe-field-grid">{entries.filter(([,v]) => v !== '' && v != null && !Array.isArray(v) && typeof v !== 'object').slice(0, 240).map(([label,value]) =>
        <div className="npe-readonly" key={label}><span>{label}</span><strong>{readable(value)}</strong></div>)}</div>
    </section>)}
    <section className="npe-question-card"><h4>Apoderados</h4>
      {(questionnaire.grantees || []).length ? questionnaire.grantees.map((p,i)=><div className="npe-registered" key={p.id || i}>
        <strong>{p.fullName || `Apoderado ${i+1}`}</strong><span>{p.nationality || '—'} · RFC: {p.rfc || '—'} · CURP: {p.curp || '—'}</span>
      </div>) : <p>Sin apoderados registrados.</p>}
    </section>
    <section className="npe-question-card"><h4>Anexos recibidos</h4>
      {(questionnaire.clientDocuments || []).length ? questionnaire.clientDocuments.map((doc,i) =>
        <div className="npe-registered" key={doc.key || i}><strong>{describeField(doc.key)}</strong><span>{doc.filename}</span></div>
      ) : <p>Sin anexos.</p>}
    </section>
    <section className="npe-question-card"><h4>Accionistas y poderes</h4>
      {(questionnaire.assemblyData?.shareholders || []).map((p,i)=><div className="npe-registered" key={p.id || i}><strong>{p.name || `Accionista ${i+1}`}</strong><span>{p.shares || '—'} acciones · {p.shareSeries || '—'}</span></div>)}
      {(questionnaire.powerSelections || []).map((p,i)=><div className="npe-registered" key={p._id || i}><strong>{p.powerCatalogId?.displayName || `Poder ${i+1}`}</strong><span>{p.customActsDescription || (p.hasLimitations ? 'Con limitantes' : 'Sin limitantes')}</span></div>)}
    </section>
  </div>;
}

export default function NotarialProjectEditor({ poder, onRefresh, canApproveProject = false, embedded = false }) {
  const fetcher = useRoleViewFetch();
  const base = API + '/api/notario-dashboard/expedientes/' + poder._id + '/notarial/editor';
  const [open, setOpen] = useState(embedded);
  const [caseView, setCaseView] = useState('pdf');
  const [data, setData] = useState(null);
  const [paragraphs, setParagraphs] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [pendingPlaceholders, setPendingPlaceholders] = useState({});
  const [tab, setTab] = useState('questionnaire');
  const [fullText, setFullText] = useState('');
  const [fullTextError, setFullTextError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [observations, setObservations] = useState('');
  const [versions, setVersions] = useState([]);
  const [preview, setPreview] = useState({ url: '', loading: false, error: '' });
  const previewURL = useRef('');
  const previewSerial = useRef(0);
  const editSerial = useRef(0);
  const latest = useRef({ paragraphs: [], overrides: {} });
  const currentDraft = data?.draft;

  const request = useCallback(async (path = '', options = {}) => {
    const response = await fetcher(base + path, {
      ...options, headers: { 'x-auth-token': token(), ...options.headers },
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.msg || 'No se pudo completar la operación.');
    }
    return response;
  }, [base, fetcher]);

  const viewPDF = useCallback(async () => {
    const sequence = ++previewSerial.current;
    setPreview(s => ({ ...s, loading: true, error: '' }));
    try {
      const response = await request('/preview-pdf');
      const blob = await response.blob();
      if (sequence !== previewSerial.current) return;
      const url = URL.createObjectURL(blob);
      if (previewURL.current) URL.revokeObjectURL(previewURL.current);
      previewURL.current = url;
      setPreview({ url, loading: false, error: '' });
    } catch (e) { if (sequence === previewSerial.current) setPreview(s => ({ ...s, loading: false, error: e.message })); }
  }, [request]);

  const load = useCallback(async () => {
    setError(''); setLoading(true);
    try {
      const result = await (await request()).json();
      const para = result.draft?.paragraphs || [];
      const values = result.draft?.fieldOverrides || {};
      const normalized = values instanceof Array ? Object.fromEntries(values) : values;
      setData(result); setParagraphs(para); setOverrides(normalized); setPendingPlaceholders({});
      latest.current = { paragraphs: para, overrides: normalized };
      setFullText(para.map(p => p.text).join('\n\n')); setFullTextError('');
      setDirty(false); setTab('questionnaire');
      await viewPDF();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [request, viewPDF]);

  useEffect(() => {
    if (open) load();
    return () => { previewSerial.current += 1; };
  }, [open, poder._id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Al registrar libro, folio, número o fecha desde el proceso notarial, los
  // marcadores se actualizan únicamente si no hay cambios locales por guardar.
  const protocolSignature = [
    poder.notarialWorkflow?.protocolBook, poder.notarialWorkflow?.protocolFolio,
    poder.notarialWorkflow?.deedNumber, poder.notarialWorkflow?.deedDate,
  ].join('|');
  const lastProtocol = useRef(protocolSignature);
  useEffect(() => {
    if (open && !dirty && lastProtocol.current !== protocolSignature) {
      lastProtocol.current = protocolSignature;
      load();
    }
  }, [open, dirty, protocolSignature]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (previewURL.current) URL.revokeObjectURL(previewURL.current); }, []);

  const change = (items, nextOverrides = overrides) => {
    const updated = { paragraphs: items, overrides: nextOverrides };
    latest.current = updated;
    setParagraphs(items); setOverrides(nextOverrides);
    setFullText(items.map(p=>p.text).join('\n\n'));
    setDirty(true); editSerial.current += 1;
    setNotice('');
  };
  const changeComplete = value => {
    setFullText(value);
    const pieces = value.split('\n\n');
    if (pieces.length !== paragraphs.length) {
      setFullTextError(`Se requieren ${paragraphs.length} párrafos separados por una línea en blanco; hay ${pieces.length}.`);
      return;
    }
    if (pieces.some(p => !p.trim())) { setFullTextError('Ningún párrafo puede quedar vacío.'); return; }
    setFullTextError('');
    change(paragraphs.map((p,i) => ({ ...p, text: pieces[i] })));
  };
  const replacePlaceholder = (key, value) => {
    if (!data?.canEdit) return;
    if (value.length > 2000) return setError('El valor de un marcador no puede exceder 2,000 caracteres.');
    const oldValue = textOf(Object.prototype.hasOwnProperty.call(overrides, key) ? overrides[key] : data.templateValues?.[key]);
    if (value === oldValue) {
      setPendingPlaceholders(prev => { const copy = { ...prev }; delete copy[key]; return copy; });
      return;
    }
    // Avoid replacing every single letter/digit in legal clauses. Apply only
    // completed edits on blur, and require manual review for ambiguous values.
    const safeLiteral = oldValue.length >= 4;
    const next = paragraphs.map(p => ({
      ...p, text: safeLiteral ? p.text.split(oldValue).join(value) : p.text,
    }));
    const count = safeLiteral ? paragraphs.reduce((sum, p) => sum + p.text.split(oldValue).length - 1, 0) : 0;
    setPendingPlaceholders(prev => { const copy = { ...prev }; delete copy[key]; return copy; });
    setError('');
    change(next, { ...overrides, [key]: value });
    setNotice(count > 0 ? `Actualizado en ${count} lugar(es) del proyecto.` : 'El valor quedó registrado, pero no se reemplazaron textos ambiguos. Comprueba y ajusta el párrafo en «Texto completo».');
  };

  useEffect(() => {
    if (!open || !dirty || saving || loading || fullTextError || !currentDraft || !data.canEdit) return undefined;
    const sequence = editSerial.current;
    const timer = setTimeout(async () => {
      setSaving(true); setError('');
      const snapshot = latest.current;
      try {
        const result = await (await request('', {
          method:'PUT', headers:{ 'Content-Type':'application/json' },
          body:JSON.stringify({ revision: currentDraft.revision, paragraphs: snapshot.paragraphs, fieldOverrides: snapshot.overrides }),
        })).json();
        setData(prev => ({ ...prev, draft: result.draft }));
        if (sequence === editSerial.current) { setDirty(false); setNotice('Cambios guardados en la Escritura Pública.'); }
        await viewPDF();
      } catch(e) { setError(e.message); }
      finally { setSaving(false); }
    }, 1250);
    return () => clearTimeout(timer);
  }, [open, dirty, saving, loading, fullTextError, currentDraft, data, request, viewPDF]);

  const mutate = async (path, body = {}) => {
    setError(''); setNotice('');
    try {
      const response = await request(path, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await response.json();
      if (result.draft) {
        const nextOverrides = result.draft.fieldOverrides || {};
        setData(prev => ({ ...prev, draft: result.draft }));
        setParagraphs(result.draft.paragraphs); setOverrides(nextOverrides);
        latest.current = { paragraphs: result.draft.paragraphs, overrides: nextOverrides };
        setFullText(result.draft.paragraphs.map(p => p.text).join('\n\n'));
        setDirty(false);
      }
      setNotice(path === '/save-version' ? 'Versión de recuperación guardada.'
        : path === '/submit' ? 'Escritura enviada al Notario titular para revisión.'
        : path === '/approve' ? 'Escritura aprobada por el Notario titular.'
        : 'Observaciones enviadas al Abogado de Notaría.');
      await onRefresh?.();
      await viewPDF();
    } catch(e) { setError(e.message); }
  };
  const showVersions = async () => {
    try { setVersions((await (await request('/versions')).json()).versions || []); }
    catch(e) { setError(e.message); }
  };
  const definitivePdf = async () => {
    try {
      const buffer = await (await request('/final-pdf')).blob();
      const url = URL.createObjectURL(buffer);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch(e) { setError(e.message); }
  };
  const attemptClose = () => {
    if (dirty || saving) {
      setError('Guarda los cambios antes de cerrar para no perder la revisión.'); return;
    }
    setOpen(false);
  };
  const groups = useMemo(() => {
    const result = [];
    paragraphs.forEach((p,index) => {
      const label = p.section || 'Documento';
      let group = result[result.length - 1];
      if (!group || group.label !== label) { group = { label, items: [] }; result.push(group); }
      group.items.push({ paragraph:p,index });
    });
    return result;
  }, [paragraphs]);
  const pending = currentDraft?.submittedRevision === currentDraft?.revision;
  const approved = currentDraft?.approvedRevision === currentDraft?.revision;
  const actionsDisabled = dirty || saving || Object.keys(pendingPlaceholders).length > 0 || Boolean(error) || Boolean(fullTextError) || !currentDraft || loading;
  const canEdit = Boolean(data?.canEdit);

  if (embedded) return <section className="npe-case-editor" aria-label="Sugerencia de Escritura Pública">
    <header className="npe-case-top">
      <div><span>ESCRITURA PÚBLICA · SUGERENCIA PARA REVISIÓN</span>
        <strong>{poder.folio} · {approved ? 'Proyecto aprobado' : pending ? 'Pendiente de visto bueno' : 'Proyecto en preparación'}</strong>
        <small>{!data?.canEdit && !poder.internalResponsibles?.notaryLawyerId ? 'Vista de consulta · asigna un abogado para editar' : saving ? 'Guardando cambios…' : dirty ? 'Cambios pendientes' : currentDraft ? 'Cambios guardados' : 'Documento en preparación'}</small>
      </div>
      <nav aria-label="Vista de la Escritura Pública" className="npe-case-switch">
        <button type="button" className={caseView === 'pdf' ? 'active' : ''} aria-pressed={caseView === 'pdf'} onClick={() => setCaseView('pdf')}>Vista PDF</button>
        <button type="button" className={caseView === 'full' ? 'active' : ''} aria-pressed={caseView === 'full'} onClick={() => setCaseView('full')}>Texto completo</button>
      </nav>
    </header>
    {canApproveProject && pending && !approved && <div className="npe-case-actions">
      <button type="button" className="npe-case-primary" disabled={actionsDisabled}
        onClick={() => mutate('/approve', { revision: currentDraft.revision })}>Aprobar proyecto</button>
      <label>Observaciones
        <textarea value={observations} onChange={e => setObservations(e.target.value)} placeholder="Indica qué debe corregirse" />
      </label>
      <button type="button" disabled={!observations.trim() || saving}
        onClick={() => mutate('/request-changes', { observations })}>Solicitar correcciones</button>
    </div>}
    {notice && <p className="npe-case-message" role="status">{notice}</p>}
    {error && <div className="npe-case-error" role="alert"><strong>No se pudo cargar o guardar la escritura</strong><p>{error}</p><button type="button" onClick={load}>Reintentar</button></div>}
    <div className="npe-case-content">
      {loading && !data ? <div className="npe-case-empty" role="status">Preparando la Escritura Pública sugerida…</div>
        : !data ? <div className="npe-case-empty">Todavía no hay una sugerencia de Escritura Pública disponible para este folio.</div>
        : caseView === 'full' ? <div className="npe-case-text">
            <p>Revisa y edita la Escritura Pública. El texto se guarda automáticamente y el PDF se actualiza al finalizar el guardado. El Acta de Wisby permanece intacta.</p>
            {fullTextError && <p role="alert" className="npe-error">{fullTextError}</p>}
            <textarea aria-label="Texto completo de la Escritura Pública" spellCheck="true" disabled={!canEdit || saving} value={fullText} onChange={e => changeComplete(e.target.value)} />
          </div>
        : preview.loading && !preview.url ? <div className="npe-case-empty" role="status">Generando vista PDF de la escritura…</div>
        : preview.error && !preview.url ? <div className="npe-case-empty" role="alert">{preview.error}<button type="button" onClick={viewPDF}>Reintentar PDF</button></div>
        : preview.url ? <><iframe title="Escritura Pública sugerida (borrador)" src={preview.url} />
            {(preview.loading || dirty) && <div className="npe-case-preview-note" role="status">{preview.loading ? 'Actualizando PDF…' : 'Los cambios aparecerán al terminar el guardado.'}</div>}
            {preview.error && <div className="npe-case-preview-note" role="alert">{preview.error}</div>}
          </> : <div className="npe-case-empty">No hay vista PDF disponible todavía.</div>}
    </div>
    {canEdit && <footer className="npe-case-footer">
      <span>{saving ? 'Guardando cambios…' : dirty ? 'Guardado automático pendiente' : currentDraft ? 'Cambios guardados automáticamente' : ''}</span>
      <button type="button" className="npe-case-primary" disabled={actionsDisabled || approved}
        onClick={() => mutate('/submit', { revision: currentDraft.revision })}>Enviar al Notario</button>
    </footer>}
  </section>;
  return <section className="npe-panel" aria-label="Editor de Escritura Pública">
    <div className="npe-launch">
      <div><span>ESCRITURA PÚBLICA · EDICIÓN INTERNA</span><h3>Cuestionario y proyecto de escritura</h3><p>Consulta los datos del cliente, adapta los placeholders y modifica el texto completo con vista PDF en paralelo.</p></div>
      <button type="button" onClick={() => setOpen(true)}>Abrir editor dividido</button>
    </div>
    {open && createPortal(<div className="npe-overlay" role="presentation">
      <div className="npe-shell" role="dialog" aria-modal="true" aria-label="Editor dividido de Escritura Pública">
        <header className="npe-modal-head">
          <div><span>WISBY · ESCRITURA PÚBLICA</span><h2>{poder.folio} · Revisión {currentDraft?.revision || '—'}</h2>
            <p>{approved ? 'Proyecto aprobado' : pending ? 'En revisión del Notario titular' : 'Proyecto en elaboración'} · {saving ? 'Guardando cambios…' : dirty ? 'Cambios pendientes' : 'Guardado'}</p>
          </div>
          <button type="button" aria-label="Cerrar editor de escritura" onClick={attemptClose}>×</button>
        </header>
        {error && <p className="npe-banner npe-error" role="alert">{error}</p>}
        {notice && <p className="npe-banner npe-success" role="status">{notice}</p>}
        <div className="npe-actions">
          {canEdit && <>
            <span className="npe-auto-save-status">{saving ? 'Guardando automáticamente…' : dirty ? 'Cambios pendientes' : 'Cambios guardados automáticamente'}</span>
          </>}
          {canApproveProject && pending && !approved && <>
            <button type="button" className="npe-primary" disabled={actionsDisabled} onClick={() => mutate('/approve', { revision: currentDraft.revision })}>Aprobar como Notario titular</button>
            <label className="npe-observations">Observaciones<textarea value={observations} onChange={e => setObservations(e.target.value)} placeholder="Indica los cambios necesarios" /></label>
            <button type="button" disabled={!observations.trim()} onClick={() => mutate('/request-changes', { observations })}>Devolver con observaciones</button>
          </>}
          <button type="button" onClick={showVersions}>Historial</button>
          <button type="button" disabled={!approved} onClick={definitivePdf}>PDF definitivo</button>
        </div>
        {versions.length > 0 && <details className="npe-versions" open><summary>Versiones guardadas</summary><ol>{versions.map(v => <li key={v._id}>Revisión {v.revision} · {v.kind} · {new Date(v.createdAt).toLocaleString('es-MX')}</li>)}</ol></details>}
        <div className="npe-layout">
          <aside className="npe-left" aria-label="Cuestionario y edición de la Escritura Pública">
            <nav className="npe-tabs" aria-label="Modos de edición de escritura">
              {[['questionnaire','Cuestionario'],['placeholders','Placeholders'],['full','Texto completo'],['paragraphs','Párrafos']].map(([key,label])=>
                <button key={key} type="button" className={tab === key ? 'active':''} aria-pressed={tab === key} onClick={() => setTab(key)}>{label}</button>)}
            </nav>
            <div className="npe-scroll">
              {loading && <p role="status">Cargando proyecto de escritura…</p>}
              {!loading && !data && <p role="alert">{error || 'Todavía no existe una escritura generada desde una plantilla autorizada.'}</p>}
              {data && tab === 'questionnaire' && <QuestionnaireSummary questionnaire={data.questionnaire} />}
              {data && tab === 'placeholders' && <div className="npe-placeholder-editor">
                <div className="npe-info-note"><strong>Valores vinculados a la Escritura Pública</strong>
                  <p>Los valores originales vienen del cuestionario. Editarlos reemplaza coincidencias en este proyecto, sin cambiar el Acta firmada ni la plantilla global de la notaría.</p></div>
                <div className="npe-field-grid">{Object.entries(data.templateValues || {}).filter(([key, val]) => key !== 'poderes' && textOf(val).length <= 2000).map(([key,val]) =>
                  <label key={key} className="npe-placeholder"><span>{describeField(key)}</span><small>Original: {textOf(val).slice(0,150)}</small>
                    <textarea rows={key === 'apoderados' ? 4 : 2} disabled={!canEdit || saving} value={Object.prototype.hasOwnProperty.call(pendingPlaceholders,key) ? pendingPlaceholders[key] : Object.prototype.hasOwnProperty.call(overrides,key) ? overrides[key] : textOf(val)}
                      onChange={e => setPendingPlaceholders(prev => ({ ...prev, [key]: e.target.value }))}
                      onBlur={e => replacePlaceholder(key, e.target.value)} /></label>)}</div>
                <p className="npe-muted">Las cláusulas jurídicas y los poderes complejos se editan en «Texto completo» o «Párrafos».</p>
              </div>}
              {data && tab === 'full' && <div className="npe-full"><div className="npe-info-note"><strong>Edición de todo el texto</strong><p>Modifica el contenido jurídico libremente. Conserva una línea en blanco entre párrafos para mantener la estructura Word original.</p></div>
                {fullTextError && <p role="alert" className="npe-error">{fullTextError}</p>}
                <textarea aria-label="Texto completo de la Escritura Pública" rows={30} spellCheck="true" disabled={!canEdit || saving}
                  value={fullText} onChange={e => changeComplete(e.target.value)} />
              </div>}
              {data && tab === 'paragraphs' && <div className="npe-document">{groups.map((group,i)=><details key={i} className="npe-group" open={i < 2}><summary>{group.label} · {group.items.length} párrafos</summary>
                {group.items.map(({paragraph,index})=><label key={paragraph.sourceIndex} className="npe-paragraph"><span>Párrafo {index+1}</span>
                  <textarea value={paragraph.text} disabled={!canEdit || saving} spellCheck="true" rows={Math.max(3,Math.min(13,Math.ceil(paragraph.text.length/110)))}
                    onChange={e => change(paragraphs.map((p,j)=>index === j ? {...p,text:e.target.value}:p))} /></label>)}</details>)}</div>}
            </div>
          </aside>
          <section className="npe-right" aria-label="Vista previa de Escritura Pública">
            <header><div><small>VISTA PREVIA DE LA ESCRITURA PÚBLICA</small><strong>Documento del folio · no es el Acta</strong></div>
              <span className="npe-auto-preview-note" role="status">{preview.loading ? 'Actualizando PDF…' : 'Vista previa automática'}</span>
            </header>
            <div className="npe-pdf-stage">
              {preview.loading && !preview.url ? <p role="status">Generando PDF del proyecto…</p>
                : preview.error && !preview.url ? <p role="alert">{preview.error}</p>
                : preview.url ? <><iframe title="Vista previa de la Escritura Pública" src={preview.url} />
                  {(preview.loading || dirty) && <div className="npe-preview-status">{preview.loading ? 'Actualizando la vista…' : 'Los cambios se reflejan después de guardar.'}</div>}
                  {preview.error && <div className="npe-preview-status" role="alert">{preview.error}</div>}
                </> : <p>La vista previa de la escritura aparecerá aquí al abrir un proyecto autorizado.</p>}
            </div>
          </section>
        </div>
        {canEdit && <footer className="npe-modal-footer">
          <span>Los cambios se guardan automáticamente y generan la vista PDF.</span>
          <button type="button" className="npe-primary" disabled={actionsDisabled || approved}
            onClick={() => mutate('/submit', { revision: currentDraft.revision })}>Enviar al Notario</button>
        </footer>}
      </div>
    </div>, document.body)}
  </section>;
}
